/**
 * The pages surface (`040-api-contract.md` → Pages). Reads only.
 *
 * There are no write functions in this file, and that is deliberate: **every mutation in
 * the app goes through `outbox.enqueue()`** (`112-offline-drafts.md` §5). A `patchPage()`
 * helper here would be a loaded gun — the one call site that forgot the outbox is the one
 * that loses the user's words. Writes are expressed as outbox entries in
 * `features/notebook/usePageMutations.ts` and `features/editor/useAutosave.ts`.
 *
 * The types are hand-written for now. `111-frontend-architecture.md` §6 requires them to
 * be GENERATED into `src/api/types.gen.ts` (`make types` → `openapi-typescript`); that
 * target needs the pages API to be live in the backend, which it is not at the time of
 * writing. When it is, re-point these aliases at `types.gen.ts` and delete the shapes.
 * They are transcribed from the contract's response examples, field for field.
 */
import { apiFetch } from '@/api/client';

/* ── The document (`031-content-format.md`) ─────────────────────────────────── */

/**
 * A ProseMirror node. Deliberately structural rather than a union of the 15 allowed node
 * types: the SCHEMA is the contract (Tiptap enforces it — that is our XSS boundary), and
 * a hand-maintained TypeScript mirror of it would be a second source of truth that drifts.
 */
export interface ProseMirrorNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: ProseMirrorNode[];
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  text?: string;
}

export interface ProseMirrorDoc {
  type: 'doc';
  content?: ProseMirrorNode[];
}

/** `031`: an empty page. Not `{ type: 'doc' }` — ProseMirror requires one block child. */
export function emptyDoc(): ProseMirrorDoc {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

/**
 * The `updatedAt` of a page the server has not told us about yet — an optimistically
 * created row whose `POST` is still in the outbox.
 *
 * It must NOT be a client-invented timestamp. `If-Unmodified-Since` means *"I based this
 * edit on server version X"*, and inventing an X we never saw is a lie the server catches:
 * it compares the header against the real `updated_at` from the create, finds a mismatch,
 * and answers `412` — so the FIRST save of every brand-new page would open a conflict
 * banner over a page nobody else has ever touched.
 *
 * The honest answer is that there is no precondition to send. Nothing can have raced us:
 * the create is the only prior write, and the outbox's FIFO order guarantees it lands
 * first. `useAutosave` omits the header while the base is this value, and `OutboxBridge`
 * replaces it with the real one the moment the create responds.
 */
export const UNCONFIRMED = '';

/* ── The DTOs ───────────────────────────────────────────────────────────────── */

/** A row of `GET /api/pages/tree`. Flat — the client builds the tree from `parentId`. */
export interface TreePage {
  id: string;
  parentId: string | null;
  position: string;
  title: string;
  icon: string | null;
  isFavorite: boolean;
  hasChildren: boolean;
  dueAt: string | null;
  updatedAt: string;
}

export interface TreeResponse {
  pages: TreePage[];
}

/** The tag as it is embedded in a page payload — no `pageCount` (that is the tag list's). */
export interface PageTag {
  id: string;
  name: string;
  /** Optional `#rrggbb` (`030-data-model.md`: `color varchar(7) NULL`). Never a semantic. */
  color: string | null;
}

export interface Breadcrumb {
  id: string;
  title: string;
}

export interface PageDto {
  id: string;
  parentId: string | null;
  position: string;
  title: string;
  icon: string | null;
  content: ProseMirrorDoc;
  contentVersion: number;
  dueAt: string | null;
  dueAllDay: boolean;
  isFavorite: boolean;
  isArchived: boolean;
  tags: PageTag[];
  /** Ancestors, root-first, EXCLUDING the page itself. Derived server-side from `path`. */
  breadcrumb: Breadcrumb[];
  createdAt: string;
  updatedAt: string;
}

/** `POST /api/pages/{id}/move` → the moved row only. `depth` may have changed. */
export interface MoveResponse {
  id: string;
  parentId: string | null;
  position: string;
  depth: number;
}

/** Archive and restore cascade to the subtree and return what they touched (`040`). */
export interface ArchiveResponse {
  archived: string[];
}

export interface RestoreResponse {
  restored: { id: string; parentId: string | null; position: string; depth: number }[];
  /** True when the parent was still archived and the page was re-parented to root (060 §4). */
  reparented: boolean;
}

/* ── Backlinks (`040` → Backlinks, `070` §7.6) ──────────────────────────────── */

/** A page that links HERE. Derived server-side from `page_link`; never sent by a client. */
export interface Backlink {
  sourcePageId: string;
  sourceTitle: string;
  icon: string | null;
  breadcrumb: Breadcrumb[];
}

/** A `[[…]]` on THIS page that does not point at a real page yet. The to-do list. */
export interface UnresolvedLink {
  targetTitle: string;
}

export interface BacklinksResponse {
  backlinks: Backlink[];
  unresolved: UnresolvedLink[];
}

/* ── Reads ──────────────────────────────────────────────────────────────────── */

/** The sidebar's only fetch. The whole non-archived tree, in one shot. Not paginated. */
export function getTree(): Promise<TreeResponse> {
  return apiFetch<TreeResponse>('/api/pages/tree');
}

/** The trash view. Fetched lazily, on first expand (`060` §5). */
export function getArchivedTree(): Promise<TreeResponse> {
  return apiFetch<TreeResponse>('/api/pages/tree?archived=true');
}

export function getPage(id: string): Promise<PageDto> {
  return apiFetch<PageDto>(`/api/pages/${id}`);
}

/**
 * The killer feature (`070` §7.6). For an NPC page, this response IS the page's real
 * content: every session it has ever appeared in, assembled with zero curation.
 */
export function getBacklinks(id: string): Promise<BacklinksResponse> {
  return apiFetch<BacklinksResponse>(`/api/pages/${id}/backlinks`);
}

/* ── Write bodies (the shapes the outbox carries) ───────────────────────────── */

export interface CreatePageBody {
  /** Client-generated ULID. The server never invents one for a client-originated create. */
  id: string;
  parentId: string | null;
  position: string;
  title: string;
  icon?: string | null;
}

export interface UpdatePageBody {
  title?: string;
  icon?: string | null;
  content?: ProseMirrorDoc;
  isFavorite?: boolean;
  /**
   * A FULL REPLACEMENT of the page's tag set (`040`, `070` §6). There is no
   * add-one/remove-one endpoint: one writer, one shape, no drift.
   */
  tagIds?: string[];
}

export interface MovePageBody {
  parentId: string | null;
  position: string;
}
